import { Typography, Tag, CircularProgress, WidgetEmptyErrorPlaceholder } from "@ann/web-components";
import { useVehicleFiSummary } from "../../hooks/useVehicleFiSummary";
import { FiSalesContentContainer, FiSalesTopContainer, FiSalesTopColumn, FiSalesBottomContainer, FiSalesSectionContainer, FiSalesSectionRow, FiSalesSectionRowLeft, SourceInfoContainer } from "./FiSalesContent.styled";

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "N/A";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const getStatusColor = (status) => {
  if (!status) return "neutral";
  switch (status.toUpperCase()) {
    case "FUNDED":
      return "success";
    case "PENDING":
      return "warning";
    case "CANCELLED":
      return "alert";
    default:
      return "informative";
  }
};

const formatCategoryTag = (category) => {
  if (!category) return "Finance · New";
  return category === "RETAIL" ? "Finance · New" : `Finance · ${category}`;
};

const formatCashTrade = (vehicleModel, allowance, payoff) => {
  const parts = [];
  if (vehicleModel) parts.push(vehicleModel);
  if (allowance !== null && allowance !== undefined) parts.push(`Allowance ${formatCurrency(allowance)}`);
  if (payoff !== null && payoff !== undefined) parts.push(`Payoff ${formatCurrency(payoff)}`);
  return parts.length > 0 ? parts.join(" • ") : "N/A";
};

const FiSalesContent = ({ partyUuid, vehicleUuid }) => {
  const { summary, loading, error, isEmpty, refetch } = useVehicleFiSummary(partyUuid, vehicleUuid);

  if (loading) {
    return (
      <FiSalesContentContainer>
        <CircularProgress size="medium" />
      </FiSalesContentContainer>
    );
  }

  if (error) {
    return (
      <WidgetEmptyErrorPlaceholder
        buttonText="Retry"
        icon="error"
        message="We couldn't load F&I Sales. Please try again."
        onClick={refetch}
        showButton
        title="Something went wrong"
      />
    );
  }

  if (isEmpty || !summary) {
    return (
      <WidgetEmptyErrorPlaceholder
        icon="attachMoney"
        message="Warranty and protection plans will appear once available."
        showButton={false}
        title="No F&I records found"
      />
    );
  }

  return (
    <FiSalesContentContainer>
      <FiSalesTopContainer>
        <FiSalesTopColumn>
          <Typography category="subtitle" weight="bold" color="subtitle" size="small">
            Stock
          </Typography>
          <Typography category="body" weight="regular" color="body" size="medium">
            {summary.stock_number ? `#${summary.stock_number}` : "N/A"}
          </Typography>
        </FiSalesTopColumn>
        <FiSalesTopColumn>
          <Typography category="subtitle" weight="bold" color="subtitle" size="small">
            Deal
          </Typography>
          <Typography category="body" weight="regular" color="body" size="medium">
            {summary.deal_number ? `#${summary.deal_number}` : "N/A"}
          </Typography>
        </FiSalesTopColumn>
        <FiSalesTopColumn>
          <Tag color="secondary" size="small" text={formatCategoryTag(summary.category)} />
        </FiSalesTopColumn>
      </FiSalesTopContainer>
      <FiSalesBottomContainer>
        <FiSalesSectionContainer>
          <FiSalesSectionRow>
            <Typography category="subtitle" weight="bold" color="subtitle" size="small">
              Status
            </Typography>
            <Tag color={getStatusColor(summary.status)} size="small" text={summary.status || "N/A"} />
          </FiSalesSectionRow>
          <FiSalesSectionRow>
            <Typography category="subtitle" weight="bold" color="subtitle" size="small">
              Contract
            </Typography>
            <Typography category="body" weight="regular" color="body" size="medium">
              {formatDate(summary.contract)}
            </Typography>
          </FiSalesSectionRow>
          {summary.buyer && (
            <FiSalesSectionRow>
              <Typography category="subtitle" weight="bold" color="subtitle" size="small">
                Buyer
              </Typography>
              <FiSalesSectionRowLeft>
                {summary.buyer.full_name && (
                  <Typography category="body" weight="regular" color="body" size="medium">
                    {summary.buyer.full_name}
                  </Typography>
                )}
                {summary.buyer.phone && (
                  <Typography category="body" weight="regular" color="body" size="medium">
                    {summary.buyer.phone}
                  </Typography>
                )}
                {summary.buyer.email && (
                  <Typography category="body" weight="regular" color="body" size="medium">
                    {summary.buyer.email}
                  </Typography>
                )}
              </FiSalesSectionRowLeft>
            </FiSalesSectionRow>
          )}
        </FiSalesSectionContainer>
        {summary.co_buyer && (
          <FiSalesSectionContainer>
            <FiSalesSectionRow>
              <Typography category="subtitle" weight="bold" color="subtitle" size="small">
                Co-Buyer
              </Typography>
              <FiSalesSectionRowLeft>
                {summary.co_buyer.full_name && (
                  <Typography category="body" weight="regular" color="body" size="medium">
                    {summary.co_buyer.full_name}
                  </Typography>
                )}
                {summary.co_buyer.phone && (
                  <Typography category="body" weight="regular" color="body" size="medium">
                    {summary.co_buyer.phone}
                  </Typography>
                )}
                {summary.co_buyer.email && (
                  <Typography category="body" weight="regular" color="body" size="medium">
                    {summary.co_buyer.email}
                  </Typography>
                )}
              </FiSalesSectionRowLeft>
            </FiSalesSectionRow>
          </FiSalesSectionContainer>
        )}
        {summary.lender && (
          <FiSalesSectionContainer>
            <FiSalesSectionRow>
              <Typography category="subtitle" weight="bold" color="subtitle" size="small">
                Lender
              </Typography>
              <FiSalesSectionRowLeft>
                <Typography category="body" weight="regular" color="body" size="medium">
                  {summary.lender}
                </Typography>
              </FiSalesSectionRowLeft>
            </FiSalesSectionRow>
          </FiSalesSectionContainer>
        )}
        <FiSalesSectionContainer>
          {summary.sale_person && (
            <FiSalesSectionRow>
              <Typography category="subtitle" weight="bold" color="subtitle" size="small">
                Sales Person
              </Typography>
              <Typography category="body" weight="regular" color="body" size="medium">
                {summary.sale_person}
              </Typography>
            </FiSalesSectionRow>
          )}
          {summary.sales_manager && (
            <FiSalesSectionRow>
              <Typography category="subtitle" weight="bold" color="subtitle" size="small">
                Sales Manager
              </Typography>
              <Typography category="body" weight="regular" color="body" size="medium">
                {summary.sales_manager}
              </Typography>
            </FiSalesSectionRow>
          )}
          {summary.finance_manager && (
            <FiSalesSectionRow>
              <Typography category="subtitle" weight="bold" color="subtitle" size="small">
                Finance Manager
              </Typography>
              <Typography category="body" weight="regular" color="body" size="medium">
                {summary.finance_manager}
              </Typography>
            </FiSalesSectionRow>
          )}
        </FiSalesSectionContainer>
        <FiSalesSectionContainer>
          {summary.front_gross !== null && summary.front_gross !== undefined && (
            <FiSalesSectionRow>
              <Typography category="subtitle" weight="bold" color="subtitle" size="small">
                Front Gross
              </Typography>
              <Typography category="body" weight="regular" color="body" size="medium">
                {formatCurrency(summary.front_gross)}
              </Typography>
            </FiSalesSectionRow>
          )}
          {summary.back_gross !== null && summary.back_gross !== undefined && (
            <FiSalesSectionRow>
              <Typography category="subtitle" weight="bold" color="subtitle" size="small">
                Back Gross
              </Typography>
              <Typography category="body" weight="regular" color="body" size="medium">
                {formatCurrency(summary.back_gross)}
              </Typography>
            </FiSalesSectionRow>
          )}
          {summary.total_gross !== null && summary.total_gross !== undefined && (
            <FiSalesSectionRow>
              <Typography category="subtitle" weight="bold" color="subtitle" size="small">
                Total
              </Typography>
              <Typography category="body" weight="regular" color="body" size="medium">
                {formatCurrency(summary.total_gross)}
              </Typography>
            </FiSalesSectionRow>
          )}
        </FiSalesSectionContainer>
        <FiSalesSectionContainer>
          {summary.sales_price !== null && summary.sales_price !== undefined && (
            <FiSalesSectionRow>
              <Typography category="subtitle" weight="bold" color="subtitle" size="small">
                Sale Price
              </Typography>
              <Typography category="body" weight="regular" color="body" size="medium">
                {formatCurrency(summary.sales_price)}
              </Typography>
            </FiSalesSectionRow>
          )}
          {(summary.vehicle_model || summary.vehicle_allowance !== null || summary.vehicle_payoff !== null) && (
            <FiSalesSectionRow>
              <Typography category="subtitle" weight="bold" color="subtitle" size="small">
                Cash / Trade
              </Typography>
              <Typography category="body" weight="regular" color="body" size="medium">
                {formatCashTrade(summary.vehicle_model, summary.vehicle_allowance, summary.vehicle_payoff)}
              </Typography>
            </FiSalesSectionRow>
          )}
          {summary.finance_amount && (
            <FiSalesSectionRow>
              <Typography category="subtitle" weight="bold" color="subtitle" size="small">
                Financed Amount
              </Typography>
              <Typography category="body" weight="regular" color="body" size="medium">
                {formatCurrency(parseFloat(summary.finance_amount))}
              </Typography>
            </FiSalesSectionRow>
          )}
        </FiSalesSectionContainer>
        <SourceInfoContainer>
          <Typography category="body" weight="regular" color="body" size="medium">
            Source • CDK
          </Typography>
        </SourceInfoContainer>
      </FiSalesBottomContainer>
    </FiSalesContentContainer>
  );
};

export default FiSalesContent;